import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../css/contactus.scss";

const initialFormData = {
  name: "",
  email: "",
  enquiry: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((current) => ({
        ...current,
        [name]: "",
      }));
    }

    if (showSuccess) {
      setShowSuccess(false);
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) {
      nextErrors.name = "Please enter your name.";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!formData.enquiry) {
      nextErrors.enquiry = "Please choose an enquiry type.";
    }

    if (!formData.message.trim()) {
      nextErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please enter at least 10 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setShowSuccess(false);

      const firstInvalidField = event.currentTarget.querySelector(
        `[name="${Object.keys(nextErrors)[0]}"]`
      );
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setFormData(initialFormData);
    setShowSuccess(true);
  };

  return (
    <main className="contact-page">
      <section className="contact-hero" aria-labelledby="contact-title">
        <p className="contact-kicker">Customer care</p>
        <h1 id="contact-title">How can we help?</h1>
        <p className="contact-hero__intro">
          Questions about fit, an order or a product? Share the details below
          or email east@nartvmora.site.
        </p>
      </section>

      <section className="contact-shell contact-layout" aria-label="Contact options">
        <aside className="contact-guide" aria-labelledby="contact-guide-title">
          <p className="contact-kicker">Before you write</p>
          <h2 id="contact-guide-title">A quick answer may already be here.</h2>
          <p>
            Find delivery timing, return conditions and practical order
            information in our help pages.
          </p>
          <nav className="contact-guide__links" aria-label="Customer help links">
            <Link to="/shipping">
              Shipping information <span aria-hidden="true">&rarr;</span>
            </Link>
            <Link to="/returns">
              Returns information <span aria-hidden="true">&rarr;</span>
            </Link>
          </nav>
          <div className="contact-guide__detail">
            <span>Helpful details to include</span>
            <p>
              For an order enquiry, add the order reference and the email used
              at checkout. Do not include payment-card details.
            </p>
          </div>
        </aside>

        <div className="contact-form-panel">
          <div
            className="contact-status"
            aria-live="polite"
            aria-atomic="true"
          >
            {showSuccess && (
              <div className="contact-success" role="status">
                <strong>Message ready.</strong>
                <p>
                  Please send the same details to east@nartvmora.site so our team can respond.
                </p>
              </div>
            )}
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-form__row">
              <div className="contact-field">
                <label htmlFor="contact-name">
                  Name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? "contact-name-error" : undefined}
                  required
                />
                {errors.name && (
                  <span className="contact-field__error" id="contact-name-error">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="contact-field">
                <label htmlFor="contact-email">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={
                    errors.email ? "contact-email-error" : undefined
                  }
                  required
                />
                {errors.email && (
                  <span className="contact-field__error" id="contact-email-error">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>

            <div className="contact-field">
              <label htmlFor="contact-enquiry">
                Enquiry <span aria-hidden="true">*</span>
              </label>
              <select
                id="contact-enquiry"
                name="enquiry"
                value={formData.enquiry}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.enquiry)}
                aria-describedby={
                  errors.enquiry ? "contact-enquiry-error" : undefined
                }
                required
              >
                <option value="">Select an enquiry type</option>
                <option value="order">Online order</option>
                <option value="product">Product or fit</option>
                <option value="returns">Returns or exchanges</option>
                <option value="stockists">Stockists</option>
                <option value="partnerships">Press or partnerships</option>
                <option value="other">Other</option>
              </select>
              {errors.enquiry && (
                <span className="contact-field__error" id="contact-enquiry-error">
                  {errors.enquiry}
                </span>
              )}
            </div>

            <div className="contact-field">
              <label htmlFor="contact-message">
                Message <span aria-hidden="true">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={
                  errors.message ? "contact-message-error" : "contact-message-hint"
                }
                required
              />
              <span className="contact-field__hint" id="contact-message-hint">
                Please do not include payment-card information.
              </span>
              {errors.message && (
                <span className="contact-field__error" id="contact-message-error">
                  {errors.message}
                </span>
              )}
            </div>

            <p className="contact-form__required">* Required fields</p>
            <button className="contact-form__submit" type="submit">
              Complete enquiry
              <span aria-hidden="true">&rarr;</span>
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
